import {SketchArcObject} from '../../../mesh/sketch/SketchArcObject';
import {AbstractSketchEdgeObject} from '../../../mesh/sketch/AbstractSketchEdgeObject';
import {SketchEllipseObject} from '../../../mesh/sketch/SketchEllipseObject';
import {SketchLineObject} from '../../../mesh/sketch/SketchLineObject';
import {SketchParabolaObject} from '../../../mesh/sketch/SketchParabolaObject';
import type {SketchPointObject} from '../../../mesh/sketch/SketchPointObject';
import {SketchSplineObject} from '../../../mesh/sketch/SketchSplineObject';
import {SketchStyleSplineObject} from '../../../mesh/sketch/SketchStyleSplineObject';
import {addObject3DToParent} from '../../../utils/scene-object/addObject3DToParent';
import type {
	SketchReferenceAppliedEndpointBinding,
	SketchReferenceCanonicalizableEdge,
	SketchReferenceEndpointRole,
	SketchReferenceTargetEndpointCanonicalizationInput,
	SketchReferenceTargetEndpointCanonicalizationState,
	SketchReferenceTargetPointRuntimeState,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceTargetEndpointCanonicalization';

/**
 * 转换实体引用目标端点统一事务。
 *
 * 该对象只管理一次候选点—边拓扑变更；关系公式和参数求解仍由既有统一参数系统负责。
 */
export class SketchReferenceTargetEndpointCanonicalization {
	/** 当前事务状态。 */
	private state: SketchReferenceTargetEndpointCanonicalizationState = 'prepared';

	/** 已实际改绑的边端点记录。 */
	private readonly appliedBindings: SketchReferenceAppliedEndpointBinding[] = [];

	/** 参与事务的点在变更前的完整可变运行时状态。 */
	private readonly originalPointStates = new Map<SketchPointObject, SketchReferenceTargetPointRuntimeState>();

	/**
	 * 创建并立即应用候选端点拓扑。
	 *
	 * @param input 端点统一事务输入。
	 */
	public constructor(private readonly input: SketchReferenceTargetEndpointCanonicalizationInput) {
		this.captureOriginalPointStates();
		this.apply();
	}

	/** 当前候选是否包含真实拓扑变化。 */
	public get changed(): boolean {
		return this.appliedBindings.length > 0;
	}

	/**
	 * 提交候选拓扑。
	 *
	 * 旧点已经在 apply 阶段移出场景，提交只关闭事务，避免成功求解后再次修改运行时对象。
	 */
	public commit(): void {
		if (this.state !== 'prepared') {
			return;
		}
		this.state = 'committed';
	}

	/**
	 * 回滚候选拓扑。
	 *
	 * 先恢复边端点身份，再恢复所有参与点的原父级与锚点绑定。
	 */
	public rollback(): void {
		if (this.state !== 'prepared') {
			return;
		}

		// 端点 setter 可能读取并同步点位，先恢复点值再恢复对象身份。
		this.restoreOriginalPointValues();
		[...this.appliedBindings].reverse().forEach((binding) => {
			binding.roles.forEach((role) => {
				this.setEdgeEndpoint(binding.edge, role, binding.sourcePoint);
			});
		});
		// setter 还会同步辅助状态和控制点角色，改绑完成后再次覆盖为事务前事实。
		this.restoreOriginalPointValues();
		this.restoreOriginalPointParents();
		this.refreshRestoredEdges();
		this.input.bindingChanges.forEach((change) => {
			this.input.targetAnchorBindings.set(
				change.sourceAnchorId,
				[...change.previousTargetPoints],
			);
		});
		this.state = 'rolled-back';
	}

	/** 捕获所有旧点与规范点的原可变运行时状态。 */
	private captureOriginalPointStates(): void {
		this.input.canonicalPointsToPromote.forEach((point) => {
			this.capturePointState(point);
		});
		this.input.assignments.forEach((assignment) => {
			this.capturePointState(assignment.sourcePoint);
			this.capturePointState(assignment.canonicalPoint);
			assignment.connectedEdges.forEach((edge) => {
				if (!(edge instanceof AbstractSketchEdgeObject)) {
					return;
				}
				edge.getRelatedSketchPointsSnapshot().forEach((relatedPoint) => {
					this.capturePointState(relatedPoint);
				});
			});
		});
	}

	/**
	 * 捕获单个点的事务前状态。
	 *
	 * @param point 参与端点改绑的点。
	 */
	private capturePointState(point: SketchPointObject): void {
		if (this.originalPointStates.has(point)) {
			return;
		}
		const position = point.getPosition();
		this.originalPointStates.set(point, {
			point,
			parent: point.parent,
			position: [position.x, position.y, position.z],
			isAuxiliaryObject: point.isAuxiliaryObject,
			boxSelectionRole: point.boxSelectionRole,
			ellipseControlPointRole: point.ellipseControlPointRole,
			slotControlPointRole: point.slotControlPointRole,
			parabolaControlPointRole: point.parabolaControlPointRole,
			isActiveForSnap: point.isActiveForSnap,
			visible: point.visible,
		});
	}

	/** 应用全部旧点到规范点的边端点改绑。 */
	private apply(): void {
		try {
			this.input.assignments.forEach((assignment) => {
				assignment.connectedEdges.forEach((edge) => {
					const roles = this.resolveEndpointRoles(edge, assignment.sourcePoint);
					if (roles.length <= 0) {
						throw new Error(`转换实体引用曲线不支持共享端点改绑: ${edge.persistentId}`);
					}
					// 先登记再改绑，确保任一端点 setter 抛错时 catch 分支仍能完整逆向恢复。
					this.appliedBindings.push({
						edge,
						sourcePoint: assignment.sourcePoint,
						canonicalPoint: assignment.canonicalPoint,
						roles,
					});
					roles.forEach((role) => {
						this.setEdgeEndpoint(edge, role, assignment.canonicalPoint);
					});
				});
				if (assignment.sourcePoint.getConnectedEdgesSnapshot().length <= 0) {
					assignment.sourcePoint.parent?.remove(assignment.sourcePoint);
				}
			});
			this.input.bindingChanges.forEach((change) => {
				this.input.targetAnchorBindings.set(change.sourceAnchorId, [change.canonicalPoint]);
			});
			this.input.canonicalPointsToPromote.forEach((canonicalPoint) => {
				if (canonicalPoint.parent !== this.input.targetSketch) {
					addObject3DToParent(canonicalPoint, this.input.targetSketch);
				}
			});
			/**
			 * keeper 只保留既有点身份；其坐标必须来自当前投影候选。先完成全部端点改绑，
			 * 再统一写点并刷新连接边，避免逐边执行顺序决定共享顶点最终位置。
			 */
			const movedCanonicalPoints = new Map<string, SketchPointObject>();
			this.input.bindingChanges.forEach((change) => {
				const currentPosition = change.canonicalPoint.getPosition();
				const nextPosition = currentPosition.clone().set(...change.currentProjectionPosition);
				if (currentPosition.equals(nextPosition)) {
					return;
				}
				change.canonicalPoint.syncPositionSilently(nextPosition);
				movedCanonicalPoints.set(change.canonicalPoint.uuid, change.canonicalPoint);
			});
			const affectedEdges = new Map<string, AbstractSketchEdgeObject>();
			movedCanonicalPoints.forEach((canonicalPoint) => {
				canonicalPoint.getConnectedEdgesSnapshot().forEach((edge) => {
					affectedEdges.set(edge.uuid, edge);
				});
			});
			affectedEdges.forEach(edge => edge.refreshGeometryOnly());
		} catch (error) {
			this.rollback();
			throw error;
		}
	}

	/** 恢复参与点在事务前的坐标与角色状态。 */
	private restoreOriginalPointValues(): void {
		this.originalPointStates.forEach((state) => {
			state.point.syncPositionSilently(
				state.point.getPosition().set(...state.position),
			);
			state.point.isAuxiliaryObject = state.isAuxiliaryObject;
			state.point.boxSelectionRole = state.boxSelectionRole;
			state.point.ellipseControlPointRole = state.ellipseControlPointRole;
			state.point.slotControlPointRole = state.slotControlPointRole;
			state.point.parabolaControlPointRole = state.parabolaControlPointRole;
			state.point.isActiveForSnap = state.isActiveForSnap;
			state.point.visible = state.visible;
		});
	}

	/** 恢复参与点在事务前的父级。 */
	private restoreOriginalPointParents(): void {
		this.originalPointStates.forEach((state) => {
			if (!state.parent) {
				state.point.parent?.remove(state.point);
				return;
			}
			if (state.point.parent !== state.parent) {
				addObject3DToParent(state.point, state.parent);
			}
		});
	}

	/** 刷新回滚后全部受影响边的几何缓存。 */
	private refreshRestoredEdges(): void {
		const restoredEdges = new Set<AbstractSketchEdgeObject>();
		this.appliedBindings.forEach((binding) => {
			if (binding.edge instanceof AbstractSketchEdgeObject) {
				restoredEdges.add(binding.edge);
			}
		});
		this.originalPointStates.forEach((state) => {
			state.point.getConnectedEdgesSnapshot().forEach(edge => restoredEdges.add(edge));
		});
		restoredEdges.forEach((edge) => {
			edge.refreshGeometryOnly();
		});
	}

	/**
	 * 解析草图边中由指定点承担的端点角色。
	 *
	 * @param edge 草图边。
	 * @param point 待解析点。
	 * @returns 命中的起点、终点角色。
	 */
	private resolveEndpointRoles(
		edge: SketchReferenceCanonicalizableEdge,
		point: SketchPointObject,
	): SketchReferenceEndpointRole[] {
		const roles: SketchReferenceEndpointRole[] = [];
		if (edge instanceof SketchLineObject) {
			if (edge.startPoint === point) roles.push('start');
			if (edge.endPoint === point) roles.push('end');
			return roles;
		}
		if (
			edge instanceof SketchArcObject
			|| edge instanceof SketchEllipseObject
			|| edge instanceof SketchParabolaObject
		) {
			if (edge.getStartPoint() === point) roles.push('start');
			if (edge.getEndPoint() === point) roles.push('end');
			return roles;
		}
		if (edge instanceof SketchSplineObject || edge instanceof SketchStyleSplineObject) {
			const controlPoints = edge.getControlPoints();
			if (controlPoints[0] === point) roles.push('start');
			if (controlPoints[controlPoints.length - 1] === point) roles.push('end');
		}
		return roles;
	}

	/**
	 * 按语义角色设置草图边端点。
	 *
	 * @param edge 草图边。
	 * @param role 起点或终点角色。
	 * @param point 新端点。
	 */
	private setEdgeEndpoint(
		edge: SketchReferenceCanonicalizableEdge,
		role: SketchReferenceEndpointRole,
		point: SketchPointObject,
	): void {
		if (edge instanceof SketchLineObject) {
			if (role === 'start') edge.setStartPoint(point);
			else edge.setEndPoint(point);
			return;
		}
		if (edge instanceof SketchArcObject) {
			const definition = edge.getArcDefinition();
			if (role === 'start') edge.setStartPoint(point, definition);
			else edge.setEndPoint(point, definition);
			return;
		}
		if (edge instanceof SketchEllipseObject || edge instanceof SketchParabolaObject) {
			if (role === 'start') edge.setStartPoint(point);
			else edge.setEndPoint(point);
			return;
		}
		if (edge instanceof SketchSplineObject || edge instanceof SketchStyleSplineObject) {
			throw new Error(`转换实体引用样条端点必须通过 replacement rebuild 改变身份: ${edge.persistentId}`);
		}
		throw new Error(`转换实体引用曲线不支持端点事务: ${edge.persistentId}`);
	}
}
